import {
  getDefinition,
  definitions,
  validateOptions,
  validateBindings,
} from "./registry.js";
import { assertSafe, ValidationError } from "./schema.js";
import { dimensionForUnit } from "./units.js";
export class RevisionConflict extends Error {
  constructor() {
    super("Personal configuration changed; reload and propose again.");
    this.name = "RevisionConflict";
  }
}
const clone = (x) => structuredClone(x);
function flatten(value, prefix = "", out = {}) {
  for (const [k, v] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (
      v &&
      typeof v === "object" &&
      !Array.isArray(v) &&
      prefix !== "bindings"
    )
      flatten(v, path, out);
    else out[path] = v;
  }
  return out;
}
function removePaths(patch, paths) {
  const out = clone(patch);
  for (const path of paths) {
    const keys = path.split(".");
    let target = out;
    for (const k of keys.slice(0, -1)) target = target?.[k];
    if (target) delete target[keys.at(-1)];
  }
  for (const k of Object.keys(out))
    if (
      out[k] &&
      typeof out[k] === "object" &&
      !Array.isArray(out[k]) &&
      !Object.keys(out[k]).length
    )
      delete out[k];
  return out;
}
function merge(a, b) {
  const out = clone(a ?? {});
  for (const [k, v] of Object.entries(b ?? {}))
    out[k] =
      v && typeof v === "object" && !Array.isArray(v)
        ? { ...out[k], ...clone(v) }
        : clone(v);
  return out;
}
export function applyPatch(instance, patch) {
  assertSafe(patch);
  for (const k of Object.keys(patch))
    if (!["options", "bindings", "presetId", "setId", "setVersion"].includes(k))
      throw new ValidationError([
        { path: k, message: "Unsupported customization field" },
      ]);
  const next = merge(instance, patch);
  if (patch.presetId !== undefined && patch.presetId !== instance.presetId) {
    const preset = getDefinition(instance.definitionId).presets.find(
      (p) => p.id === patch.presetId,
    );
    if (!preset)
      throw new ValidationError([
        { path: "presetId", message: "Unknown preset" },
      ]);
    next.options = { ...instance.options, ...preset.options, ...patch.options };
  }
  next.options = validateOptions(instance.definitionId, next.options, true);
  validateBindings(next);
  if (next.options.unit !== "inherit") {
    const dimension = dimensionForUnit(next.options.unit);
    if (!dimension)
      throw new ValidationError([
        { path: "options.unit", message: "Unknown display unit" },
      ]);
    const binding = Object.values(next.bindings).find(
      (b) => b.dimension && b.dimension !== "any",
    );
    if (binding && binding.dimension !== dimension)
      throw new ValidationError([
        {
          path: "options.unit",
          message: "Unit is incompatible with the bound measurement",
        },
      ]);
  }
  if (
    typeof next.setId !== "string" ||
    !next.setId ||
    !Number.isInteger(next.setVersion) ||
    next.setVersion < 1
  )
    throw new ValidationError([
      { path: "setId", message: "Set ID and positive revision required" },
    ]);
  return next;
}
function primary(instance) {
  const [role, binding] = Object.entries(instance.bindings).find(
    ([, b]) => b.dimension,
  ) ||
    Object.entries(instance.bindings)[0] || ["", {}];
  return { role, ...binding };
}
export function defaultBoatSelector(instance) {
  const p = primary(instance);
  return Object.fromEntries(
    Object.entries({
      definitionId: instance.definitionId,
      dimension: p.dimension,
      reference: p.reference,
      entityId: p.entityId,
      role: p.role,
    }).filter(([, v]) => v),
  );
}
const selectorKeys = [
  "definitionId",
  "family",
  "dimension",
  "reference",
  "entityId",
  "role",
];
function validateSelector(selector) {
  assertSafe(selector);
  if (!selector || !Object.keys(selector).length)
    throw new ValidationError([
      { path: "selector", message: "Explicit compatible selector required" },
    ]);
  for (const [k, v] of Object.entries(selector))
    if (!selectorKeys.includes(k) || typeof v !== "string" || !v)
      throw new ValidationError([
        { path: `selector.${k}`, message: "Unsupported selector" },
      ]);
  if (!selector.definitionId && !selector.family && !selector.dimension)
    throw new ValidationError([
      { path: "selector", message: "Definition, family or dimension required" },
    ]);
  if (selector.definitionId) getDefinition(selector.definitionId);
  return selector;
}
function matches(selector, instance) {
  const p = primary(instance),
    d = getDefinition(instance.definitionId);
  return Object.entries(selector).every(
    ([k, v]) =>
      v === (k === "definitionId" ? d.id : k === "family" ? d.family : p[k]),
  );
}
function rank(s) {
  return s.entityId || s.role || s.reference
    ? 3
    : s.definitionId
      ? 2
      : s.dimension || s.family
        ? 1
        : 0;
}
function overlaps(a, b) {
  return selectorKeys.every((k) => !a[k] || !b[k] || a[k] === b[k]);
}
function selectorKey(s) {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(s).sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
}
const panelKey = (i) => JSON.stringify([i.boardId, i.id]);
/** Reference transactional personal store. Production MUST implement this contract on an authenticated server. */
export class MemoryPersonalStore {
  constructor(session) {
    this.session = session;
    this.records = new Map();
    this.history = new Map();
  }
  context(boatId) {
    const session = this.session();
    if (!session?.accountId || !session.boatIds?.includes(boatId))
      throw new Error("Boat access denied");
    return { session, key: JSON.stringify([session.accountId, boatId]) };
  }
  state(boatId) {
    const { key } = this.context(boatId);
    return clone(
      this.records.get(key) ?? { revision: 0, panels: {}, boat: [] },
    );
  }
  revision(boatId) {
    return this.state(boatId).revision;
  }
  resolve(instance, accountPreferences = {}) {
    const state = this.state(instance.boatId);
    let result = applyPatch(instance, { options: accountPreferences });
    const candidates = state.boat
      .filter((r) => matches(r.selector, instance))
      .sort((a, b) => rank(a.selector) - rank(b.selector));
    for (const r of candidates) result = applyPatch(result, r.patch);
    const panel = state.panels[panelKey(instance)];
    if (panel) result = applyPatch(result, panel);
    return result;
  }
  origin(instance, path) {
    const s = this.state(instance.boatId);
    if (path in flatten(s.panels[panelKey(instance)] ?? {}))
      return "personal-panel";
    if (
      s.boat.some(
        (r) => matches(r.selector, instance) && path in flatten(r.patch),
      )
    )
      return "personal-boat";
    return "baseline";
  }
  preview(
    instance,
    patch,
    { selector = defaultBoatSelector(instance), instances = [instance] } = {},
  ) {
    this.context(instance.boatId);
    applyPatch(this.resolve(instance), patch);
    validateSelector(selector);
    const targets = instances.filter(
      (i) => i.boatId === instance.boatId && matches(selector, i),
    );
    const state = this.state(instance.boatId),
      paths = Object.keys(flatten(patch));
    const issues = [];
    for (const target of targets)
      try {
        applyPatch(this.resolve(target), patch);
      } catch (error) {
        issues.push({ instanceId: target.id, message: error.message });
      }
    return {
      scopeQuestion: "Just for this panel, or for your entire boat?",
      choices: ["panel", "boat"],
      scopeRequired: true,
      revision: state.revision,
      instanceId: instance.id,
      boatId: instance.boatId,
      patch: clone(patch),
      selector: clone(selector),
      panel: { count: 1 },
      boat: {
        count: targets.length,
        futureMatching: true,
        targets: targets.map((i) => ({
          id: i.id,
          title: i.options.title,
          exceptions: paths.filter(
            (path) => path in flatten(state.panels[panelKey(i)] ?? {}),
          ),
        })),
        issues,
      },
    };
  }
  commit(
    instance,
    patch,
    {
      scope,
      selector = defaultBoatSelector(instance),
      expectedRevision,
      instances = [instance],
    } = {},
  ) {
    if (scope !== "panel" && scope !== "boat")
      throw new Error("Explicit panel or boat scope choice is required");
    const { key, session } = this.context(instance.boatId);
    const state = this.state(instance.boatId);
    if (state.revision !== expectedRevision) throw new RevisionConflict();
    const before = clone(state);
    applyPatch(this.resolve(instance), patch);
    if (scope === "panel")
      state.panels[panelKey(instance)] = merge(
        state.panels[panelKey(instance)],
        patch,
      );
    else {
      validateSelector(selector);
      if (!matches(selector, instance))
        throw new Error("Boat selector must include the initiating panel");
      const preview = this.preview(instance, patch, { selector, instances });
      if (preview.boat.issues.length)
        throw new Error(preview.boat.issues.map((i) => i.message).join("; "));
      if (typeof patch.options?.order === "number")
        throw new Error("Absolute board order is panel-specific");
      const selectorId = selectorKey(selector),
        paths = Object.keys(flatten(patch));
      for (const r of state.boat)
        if (
          selectorKey(r.selector) !== selectorId &&
          rank(r.selector) === rank(selector) &&
          overlaps(r.selector, selector) &&
          Object.keys(flatten(r.patch)).some((p) => paths.includes(p))
        )
          throw new Error("Conflicting equal-priority boat selectors");
      const existing = state.boat.find(
        (r) => selectorKey(r.selector) === selectorId,
      );
      if (existing) existing.patch = merge(existing.patch, patch);
      else state.boat.push({ selector: clone(selector), patch: clone(patch) });
      if (state.panels[panelKey(instance)])
        state.panels[panelKey(instance)] = removePaths(
          state.panels[panelKey(instance)],
          paths,
        );
    }
    state.revision++;
    const token = crypto.randomUUID();
    this.records.set(key, state);
    this.history.set(token, {
      key,
      accountId: session.accountId,
      boatId: instance.boatId,
      before,
      afterRevision: state.revision,
    });
    return {
      revision: state.revision,
      undoToken: token,
      resolved: this.resolve(instance),
      scope,
    };
  }
  reset(
    instance,
    {
      scope,
      paths,
      selector = defaultBoatSelector(instance),
      expectedRevision,
    },
  ) {
    assertSafe(paths);
    if (
      !Array.isArray(paths) ||
      !paths.length ||
      paths.some(
        (p) =>
          typeof p !== "string" ||
          !["options", "bindings", "presetId", "setId", "setVersion"].includes(
            p.split(".")[0],
          ) ||
          p
            .split(".")
            .some((k) => ["__proto__", "constructor", "prototype"].includes(k)),
      )
    )
      throw new Error("Valid field paths required");
    const { key, session } = this.context(instance.boatId),
      state = this.state(instance.boatId);
    if (state.revision !== expectedRevision) throw new RevisionConflict();
    const before = clone(state);
    if (scope === "panel")
      state.panels[panelKey(instance)] = removePaths(
        state.panels[panelKey(instance)] ?? {},
        paths,
      );
    else if (scope === "boat") {
      validateSelector(selector);
      const record = state.boat.find(
        (r) => selectorKey(r.selector) === selectorKey(selector),
      );
      if (record) record.patch = removePaths(record.patch, paths);
    } else throw new Error("Explicit scope required");
    state.revision++;
    const token = crypto.randomUUID();
    this.records.set(key, state);
    this.history.set(token, {
      key,
      accountId: session.accountId,
      boatId: instance.boatId,
      before,
      afterRevision: state.revision,
    });
    return {
      revision: state.revision,
      undoToken: token,
      resolved: this.resolve(instance),
    };
  }
  undo(boatId, token) {
    const { key, session } = this.context(boatId),
      entry = this.history.get(token);
    if (!entry || entry.key !== key || entry.accountId !== session.accountId)
      throw new Error("Undo access denied");
    const state = this.state(boatId);
    if (state.revision !== entry.afterRevision) throw new RevisionConflict();
    const restored = clone(entry.before);
    restored.revision = state.revision + 1;
    this.records.set(key, restored);
    this.history.delete(token);
    return { revision: restored.revision };
  }
  exportPersonal(boatId) {
    return this.state(boatId);
  }
  importPersonal(boatId, document, { expectedRevision, instances = [] } = {}) {
    const { key } = this.context(boatId);
    if (this.revision(boatId) !== expectedRevision)
      throw new RevisionConflict();
    assertSafe(document);
    if (
      !Number.isInteger(document.revision) ||
      !document.panels ||
      !Array.isArray(document.boat)
    )
      throw new Error("Invalid personal state");
    for (const record of document.boat) {
      validateSelector(record.selector);
      const candidates = record.selector.definitionId
        ? [getDefinition(record.selector.definitionId)]
        : definitions.filter(
            (d) =>
              !record.selector.family || record.selector.family === d.family,
          );
      for (const d of candidates)
        validateOptions(d.id, record.patch.options ?? {});
      for (const instance of instances.filter(
        (i) => i.boatId === boatId && matches(record.selector, i),
      ))
        applyPatch(instance, record.patch);
    }
    for (const [panel, patch] of Object.entries(document.panels)) {
      const identity = JSON.parse(panel);
      if (
        !Array.isArray(identity) ||
        identity.length !== 2 ||
        identity.some((s) => typeof s !== "string")
      )
        throw new Error("Invalid panel identity");
      const instance = instances.find(
        (i) => i.boatId === boatId && panelKey(i) === panel,
      );
      if (!instance)
        throw new Error(
          "Base instances are required to validate imported panel overrides",
        );
      applyPatch(instance, patch);
    }
    this.records.set(key, {
      ...clone(document),
      revision: expectedRevision + 1,
    });
  }
}
export function createAgentTools(store, lookup) {
  return {
    discover: async (query) =>
      (await import("./registry.js")).discoverControls(
        typeof query === "string" ? query : (query?.query ?? ""),
      ),
    schema: async (id) =>
      clone(
        getDefinition(typeof id === "string" ? id : id.definitionId)
          .optionsSchema,
      ),
    propose: async ({ instanceId, patch, selector }) =>
      store.preview(lookup(instanceId), patch, {
        selector,
        instances: lookup(),
      }),
    commit: async ({ instanceId, patch, scope, selector, expectedRevision }) =>
      store.commit(lookup(instanceId), patch, {
        scope,
        selector,
        expectedRevision,
        instances: lookup(),
      }),
    reset: async ({ instanceId, ...args }) =>
      store.reset(lookup(instanceId), args),
    undo: async ({ boatId, token }) => store.undo(boatId, token),
  };
}
export const agentToolDefinitions = [
  {
    name: "get_control_schema",
    description:
      "Read a definition’s full editable options schema, role metadata and version before proposing changes.",
  },
  {
    name: "discover_controls",
    description:
      "Find implemented controls and retrieve their complete editable-field schemas.",
  },
  {
    name: "propose_control_customization",
    description:
      "Preview a typed customization; returns the required panel-versus-entire-boat question and compatible targets.",
  },
  {
    name: "commit_control_customization",
    description:
      "Commit only after the user explicitly chooses panel or boat; scope is personal to the authenticated account.",
  },
  {
    name: "reset_control_customization",
    description: "Remove selected fields from the chosen personal scope.",
  },
  {
    name: "undo_control_customization",
    description:
      "Undo this authenticated account’s scoped edit with revision protection.",
  },
];

const instanceField = { type: "string", minLength: 1, maxLength: 200 };
const patchField = {
  type: "object",
  properties: {
    options: { type: "object" },
    bindings: { type: "object" },
    presetId: { type: "string" },
    setId: { type: "string" },
    setVersion: { type: "integer", minimum: 1 },
  },
  additionalProperties: false,
};
const selectorField = {
  type: "object",
  properties: Object.fromEntries(
    selectorKeys.map((k) => [k, { type: "string" }]),
  ),
  additionalProperties: false,
};
for (const tool of agentToolDefinitions) {
  const properties =
    tool.name === "get_control_schema"
      ? { definitionId: instanceField }
      : tool.name === "discover_controls"
        ? { query: { type: "string" } }
        : tool.name === "undo_control_customization"
          ? { boatId: instanceField, token: instanceField }
          : {
              instanceId: instanceField,
              patch: patchField,
              selector: selectorField,
              ...(tool.name !== "propose_control_customization"
                ? {
                    scope: { type: "string", enum: ["panel", "boat"] },
                    expectedRevision: { type: "integer", minimum: 0 },
                  }
                : {}),
              ...(tool.name === "reset_control_customization"
                ? {
                    paths: {
                      type: "array",
                      items: { type: "string" },
                      minItems: 1,
                    },
                  }
                : {}),
            };
  tool.inputSchema = {
    type: "object",
    properties,
    additionalProperties: false,
    required:
      tool.name === "get_control_schema"
        ? ["definitionId"]
        : tool.name === "discover_controls"
          ? []
          : tool.name === "undo_control_customization"
            ? ["boatId", "token"]
            : tool.name === "propose_control_customization"
              ? ["instanceId", "patch"]
              : tool.name === "reset_control_customization"
                ? ["instanceId", "scope", "expectedRevision", "paths"]
                : ["instanceId", "patch", "scope", "expectedRevision"],
  };
}
